import { ModelBase } from '../abstract/model-base.model';
import { Client } from './client.model';
import { User } from '../user/user.model';
import { Authorization } from '../authorization/authorization.model';
import { CustomProfile } from '../authorization/custom-profile.model';
import { Profile } from '../authorization/profile.model';
import { UserCustomProfile } from './user-custom-profile.model';
import { UserProfile } from './user-profile.model';
import { UserAuthorization } from './user-authorization.model';
import { AuthorizationEntity } from '../../components/authorizations/authorizations-manager/authorizations-manager.component';
import moment from 'moment';

export class ClientUser extends ModelBase implements AuthorizationEntity {

    static override CLASS_NAME: string = 'ClientUser';

    private _client!: Client;
    private _user!: User;
    private _userAuthorizations!: UserAuthorization[];
    private _userCustomProfiles!: UserCustomProfile[];
    private _userProfiles!: UserProfile[];

    private _backup!: ClientUser;

    constructor(data?: any) {
        super(data);
        this._userAuthorizations = this._userAuthorizations || [];
        this._userProfiles = this._userProfiles || [];
    }

    public addAuthorization(authorizationCode: string, dateFrom?: moment.Moment, dateTo?: moment.Moment): void {
        // Vérifie si l'autorisation existe déjà
        if (this.hasAuthorization(authorizationCode)) {
            return;
        }

        // Crée une nouvelle UserAuthorization
        const userAuthorization = UserAuthorization.init();
        const authorization = new Authorization({ code: authorizationCode });
        userAuthorization.authorization = authorization;

        // Date de début et de fin
        if (dateFrom) {
            userAuthorization.dateFrom = dateFrom;
        } else {
            userAuthorization.dateFrom = moment();
        }
        if (dateTo) {
            userAuthorization.dateTo = dateTo;
        }

        // Ajoute à la liste des autorisations utilisateur
        this._userAuthorizations.push(userAuthorization);
    }

    public addCustomProfile(profile: CustomProfile): void {
        // Création de l'affectation du profil
        const userCustomProfile = UserCustomProfile.init();
        userCustomProfile.profile = profile;
        userCustomProfile.dateFrom = moment();

        // Vérifie si le profil personnalisé existe déjà
        if (this.userCustomProfiles.some((profile) => profile.profile.id === profile.id && !profile.deleted)) {
            return;
        }

        // Ajoute le profil personnalisé à la liste
        this._userCustomProfiles.push(userCustomProfile);
    }

    public addProfile(profile: Profile): void {
        // Création de l'affectation du profil
        const userProfile = UserProfile.init();
        userProfile.profile = profile;
        userProfile.dateFrom = moment();

        // Vérifie si le profil existe déjà
        if (this.userProfiles.some((profile) => profile.profile.code === userProfile.profile.code && !profile.deleted)) {
            return;
        }

        // Ajoute le profil à la liste
        this._userProfiles.push(userProfile);
    }

    public backup(): ClientUser {
        // Crée une copie profonde des données importantes
        this._backup = ClientUser.init();
        this._backup.id = this.id;
        this._backup.client = this.client;
        this._backup.user = this.user;

        // Copie les autorisations utilisateur
        this._backup.userAuthorizations = this.userAuthorizations.map(userAuth => {
            const authCopy = new (userAuth.constructor as any)();
            authCopy.id = userAuth.id;
            authCopy.authorization = userAuth.authorization;
            authCopy.dateFrom = userAuth.dateFrom;
            authCopy.dateTo = userAuth.dateTo;
            authCopy.deleted = userAuth.deleted;
            return authCopy;
        });

        // Copie les profils utilisateur
        this._backup.userProfiles = this.userProfiles.map(userProfile => {
            const profileCopy = new (userProfile.constructor as any)();
            profileCopy.id = userProfile.id;
            profileCopy.profile = userProfile.profile;
            profileCopy.dateFrom = userProfile.dateFrom;
            profileCopy.dateTo = userProfile.dateTo;
            profileCopy.deleted = userProfile.deleted;
            return profileCopy;
        });

        // Copie les profils personnalisés
        this._backup.userCustomProfiles = this.userCustomProfiles.map(customProfile => {
            const customCopy = new (customProfile.constructor as any)();
            customCopy.id = customProfile.id;
            customCopy.profile = customProfile.profile;
            customCopy.dateFrom = customProfile.dateFrom;
            customCopy.dateTo = customProfile.dateTo;
            customCopy.deleted = customProfile.deleted;
            return customCopy;
        });

        return this._backup;
    }

    /**
     * Convertit les profils utilisateur en autorisations dans le cas d'une gestion unitaire des autorisations.
     * Cette méthode est appelée lors de la première modification pour initialiser les autorisations.
     * @returns void
     * @public
     */
    public convertProfilesToAuthorizations() {
        const profilesToConvert = this.userProfiles.filter(profile => !profile.deleted);
        for (const userProfile of profilesToConvert) {
            userProfile.deleted = true; // Marque le profil utilisateur comme supprimé
            for (const authorization of userProfile.profile.authorizations) {
                this.addAuthorization(authorization.code, userProfile.dateFrom, userProfile.dateTo);
            }
        }

        const customProfilesToConvert = this.userCustomProfiles.filter(profile => !profile.deleted);
        for (const customProfile of customProfilesToConvert) {
            customProfile.deleted = true; // Marque le profil personnalisé comme supprimé
            for (const authorization of customProfile.profile.authorizations) {
                this.addAuthorization(authorization.code, customProfile.dateFrom, customProfile.dateTo);
            }
        }
    }

    public hasAuthorization(authorizationCode: string): boolean {
        // Recherche dans les profils de l'utilisateur
        for (const userProfile of this.userProfiles) {
            if (userProfile.deleted) continue; // Ignore les profils supprimés
            for (const authorization of userProfile.profile.authorizations) {
                if (authorization.code === authorizationCode) {
                    return true;
                }
            }
        }
        // Recherche dans les profils personnalisés de l'utilisateur
        for (const customProfile of this.userCustomProfiles) {
            if (customProfile.deleted) continue; // Ignore les profils personnalisés supprimés
            for (const authorization of customProfile.profile.authorizations) {
                if (authorization.code === authorizationCode) {
                    return true;
                }
            }
        }
        // Recherche dans les autorisations de l'utilisateur
        for (const userAuthorization of this.userAuthorizations) {
            if (userAuthorization.deleted) continue; // Ignore les autorisations supprimées
            if (userAuthorization.authorization.code === authorizationCode) {
                return true;
            }
        }
        return false;
    }

    public removeAllAuthorizations(): void {
        this._removeAuthorizations();
        this._removeUserProfiles();
        this._removeCustomProfiles();
    }

    public removeAuthorization(authorizationCode: string): void {
        // Supprime l'autorisation de la liste des autorisations utilisateur, en la marquant comme supprimée
        let authorization = this._userAuthorizations.find((auth) => auth.authorization.code === authorizationCode);
        if (authorization) {
            if (authorization.new) {
                // Remove from array if it's a new authorization
                const index = this._userAuthorizations.indexOf(authorization);
                if (index > -1) {
                    this._userAuthorizations.splice(index, 1);
                }
            } else {
                // Mark as deleted if it's an existing authorization
                authorization.deleted = true;
            }
        }
    }

    public restore(): void {
        if (!this._backup) {
            return;
        }

        this.id = this._backup.id;
        this.client = this._backup.client;
        this.user = this._backup.user;

        // Restaure les autorisations utilisateur
        this._userAuthorizations = this._backup.userAuthorizations.map(userAuth => {
            const authCopy = new (userAuth.constructor as any)();
            authCopy.id = userAuth.id;
            authCopy.authorization = userAuth.authorization;
            authCopy.dateFrom = userAuth.dateFrom;
            authCopy.dateTo = userAuth.dateTo;
            authCopy.deleted = userAuth.deleted;
            return authCopy;
        });

        // Restaure les profils utilisateur
        this._userProfiles = this._backup.userProfiles.map(userProfile => {
            const profileCopy = new (userProfile.constructor as any)();
            profileCopy.id = userProfile.id;
            profileCopy.profile = userProfile.profile;
            profileCopy.dateFrom = userProfile.dateFrom;
            profileCopy.dateTo = userProfile.dateTo;
            profileCopy.deleted = userProfile.deleted;
            return profileCopy;
        });

        // Restaure les profils personnalisés
        this._userCustomProfiles = this._backup.userCustomProfiles.map(customProfile => {
            const customCopy = new (customProfile.constructor as any)();
            customCopy.id = customProfile.id;
            customCopy.profile = customProfile.profile;
            customCopy.dateFrom = customProfile.dateFrom;
            customCopy.dateTo = customProfile.dateTo;
            customCopy.deleted = customProfile.deleted;
            return customCopy;
        });
    }

    public get client(): Client {
        return this._client;
    }

    public set client(value: any) {
        this._setValue('_client', value, 'Client');
    }

    public get user(): User {
        return this._user;
    }

    public set user(value: any) {
        this._setValue('_user', value, 'User');
    }

    public get userAuthorizations(): UserAuthorization[] {
        return this._userAuthorizations;
    }

    public set userAuthorizations(value: any) {
        this._setValue('_userAuthorizations', value, 'UserAuthorization');
    }

    public get userCustomProfiles(): UserCustomProfile[] {
        return this._userCustomProfiles;
    }

    public set userCustomProfiles(value: any) {
        this._setValue('_userCustomProfiles', value, 'UserCustomProfile');
    }

    public get userProfiles(): UserProfile[] {
        return this._userProfiles;
    }

    public set userProfiles(value: any) {
        this._setValue('_userProfiles', value, 'UserProfile');
    }

    public get userProfilesString(): string {
        const activeUserProfiles = this.userProfiles.filter((userProfile: UserProfile) => !userProfile.deleted).map((userProfile: UserProfile) => userProfile.profileLabel);
        const activeCustomProfiles = this.userCustomProfiles.filter((customProfile: UserCustomProfile) => !customProfile.deleted).map((customProfile: UserCustomProfile) => customProfile.profileLabel);
        let profilesString = [...activeUserProfiles, ...activeCustomProfiles].join(', ').trim();
        if (!profilesString.length) {
            profilesString = "Rôle personnalisé"
        }
        return profilesString;
    }

    private _removeAuthorizations(): void {
        // Suppression de toute les autorisations marquées 'new'
        this._userAuthorizations = this._userAuthorizations.filter((auth) => !auth.new);
        // Marqueur de suppression pour les autres
        this._userAuthorizations.forEach((auth) => {
            auth.deleted = true;
        });
    }

    private _removeUserProfiles(): void {
        // Suppression de toute les profils marqués 'new'
        this._userProfiles = this._userProfiles.filter((profile) => !profile.new);
        // Marqueur de suppression pour les autres
        this._userProfiles.forEach((profile) => {
            profile.deleted = true;
        });
    }

    private _removeCustomProfiles(): void {
        // Suppression de toute les profils personnalisés marqués 'new'
        this._userCustomProfiles = this._userCustomProfiles.filter((customProfile) => !customProfile.new);
        // Marqueur de suppression pour les autres
        this._userCustomProfiles.forEach((customProfile) => {
            customProfile.deleted = true;
        });
    }
}
